/**
 * Origin Debug Visualization for Poker Reborn
 *
 * Debug utility that shows crosshair markers at component origin points (0, 0).
 * Helps visualize how components are positioned and scale.
 *
 * @see docs/dev/ORIGIN_DEBUG.md - Full documentation
 *
 * Usage: Add ?showOrigins=true to URL
 */

import { Container, Graphics, Text, Ticker } from 'pixi.js';

/**
 * Predefined colors for different component types.
 * Each component type gets a unique color for easy identification.
 */
export const ORIGIN_COLORS = {
  seat: 0xff0000, // Red
  community: 0x00ff00, // Green
  tablePot: 0xffd700, // Gold
  playerPot: 0xffa500, // Orange
  dealer: 0xffffff, // White
  holeCards: 0x00ffff, // Cyan
  handName: 0xff00ff, // Purple
  table: 0xffff00, // Yellow
  heroSeat: 0xff4488, // Hot Pink
  heroCards: 0x44ffcc, // Mint
  heroMeter: 0x00ff00, // Green
};

/** Cached result of URL param check */
let originDebugEnabled: boolean | null = null;

/**
 * Check if origin debug visualization is enabled via URL parameter.
 * Result is cached after first call.
 *
 * @returns true if ?showOrigins=true is in the URL
 */
export function isOriginDebugEnabled(): boolean {
  if (originDebugEnabled === null) {
    const urlParams = new URLSearchParams(window.location.search);
    originDebugEnabled = urlParams.get('showOrigins') === 'true';
  }
  return originDebugEnabled;
}

/**
 * Add a crosshair origin marker to a container.
 * Only adds marker if ?showOrigins=true is in URL.
 *
 * @param container - The container to add the marker to
 * @param color - Marker color (default: red)
 * @param size - Length of crosshair arms in pixels (default: 20)
 * @returns The created Graphics object, or null if debug is disabled
 */
export function addOriginMarker(
  container: Container,
  color: number = ORIGIN_COLORS.seat,
  size: number = 20
): Graphics | null {
  if (!isOriginDebugEnabled()) {
    return null;
  }

  const marker = new Graphics();
  marker.label = 'originMarker';
  marker.alpha = 0.8;

  // Draw crosshair
  marker.setStrokeStyle({ width: 2, color });

  // Horizontal line
  marker.moveTo(-size, 0);
  marker.lineTo(size, 0);
  marker.stroke();

  // Vertical line
  marker.moveTo(0, -size);
  marker.lineTo(0, size);
  marker.stroke();

  // Center dot
  marker.circle(0, 0, 4);
  marker.fill(color);

  // Add coordinate label (small monospace font)
  const coordLabel = new Text({
    text: '(0, 0)',
    style: {
      fill: color,
      fontSize: 10,
      fontFamily: 'Roboto Mono, monospace',
      fontWeight: 'bold',
    },
  });
  coordLabel.anchor.set(0, 0.5);
  coordLabel.x = size + 4; // Position to the right of crosshair
  coordLabel.y = 0;
  coordLabel.alpha = 0.9;
  marker.addChild(coordLabel);

  // Update coordinates on each frame (real-time tracking)
  const updateCoords = (): void => {
    // Get screen dimensions for normalized calculation
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Normalized coordinates (0-1 range) - matches layout config format
    const normX = (container.x / screenWidth).toFixed(3);
    const normY = (container.y / screenHeight).toFixed(3);

    coordLabel.text = `(${normX}, ${normY})`;
  };

  // Initial update
  updateCoords();

  // Add ticker for real-time updates
  Ticker.shared.add(updateCoords);

  // Clean up ticker when marker is destroyed
  marker.on('destroyed', () => {
    Ticker.shared.remove(updateCoords);
  });

  // Add to container at the end (on top of everything)
  container.addChild(marker);

  return marker;
}
